import { Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Input } from '@/components/shadcn/input'
import { IconColorPicker } from '@/components/common/IconColorPicker'
import { HelpLabel } from '@/components/common/help/HelpLabel'
import { HelpText } from '@/components/common/help/HelpText'
import { helpKey } from '@/lib/action-modules/help'
import { TranslateButton } from '@/components/common/translations/TranslateButton'
import type { useEntityTranslations } from '@/components/common/translations/useEntityTranslations'
import type { PlannedLanguages } from '@/components/common/translations/types'
import type { EditorActionModule } from '@/lib/action-modules/registry/types'
import { RichTextEditor } from '@/components/common/RichTextEditor'


interface Props {
  actionModule: EditorActionModule
  onChanged: (value: Partial<EditorActionModule>) => void
  translations: ReturnType<typeof useEntityTranslations>
  onUploadIcon: (file: File, dataUrl: string) => void
  onDeleteIcon: () => void
  iconBusy: boolean
  disabled: boolean
  autoTranslateAll: boolean
  getPlanned: (property: string) => PlannedLanguages
  setPlanned: (property: string, planned: PlannedLanguages) => void
}

export default function CommonPropertiesTab({
                                              actionModule,
                                              onChanged,
                                              translations,
                                              onUploadIcon,
                                              onDeleteIcon,
                                              iconBusy,
                                              disabled,
                                              autoTranslateAll,
                                              getPlanned,
                                              setPlanned,
                                            }: Props) {
  const {t} = useTranslation('action-modules-wizard')
  const g = (key: string) => t(`tabGeneral.${key}`)

  return (
    <div className="flex flex-col gap-8 p-4">
      <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2">
        <section className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <HelpLabel helpKey={helpKey('generalName')}>{g('name')}</HelpLabel>
            <div className="flex gap-2">
              <Input value={actionModule.name} onChange={e => onChanged({name: e.target.value})} disabled={disabled}/>
              <TranslateButton
                dialogTitle={g('nameTranslateTitle')}
                value={translations.get('name')}
                onChange={v => translations.set('name', v)}
                plannedLanguages={getPlanned('name')}
                onPlannedChange={(planned) => setPlanned('name', planned)}
                autoTranslateAll={autoTranslateAll}
                sourceValue={actionModule.name}
              />
            </div>
          </div>
          <Field
            label={g('description')}
            help={helpKey('generalDescription')}
            value={actionModule.description}
            onChange={v => onChanged({description: v})}
            translateTitle={g('descriptionTranslateTitle')}
            translations={translations.get('description')}
            onTranslationsChange={v => translations.set('description', v)}
            disabled={disabled}
            plannedLanguages={getPlanned('description')}
            onPlannedChange={(planned) => setPlanned('description', planned)}
            autoTranslateAll={autoTranslateAll}
          />
          <Field
            label={g('employeeDescription')}
            help={helpKey('generalEmployeeDescription')}
            value={actionModule.employeeDescription}
            onChange={v => onChanged({employeeDescription: v})}
            translateTitle={g('employeeDescriptionTranslateTitle')}
            translations={translations.get('employeeDescription')}
            onTranslationsChange={v => translations.set('employeeDescription', v)}
            disabled={disabled}
            plannedLanguages={getPlanned('employeeDescription')}
            onPlannedChange={(planned) => setPlanned('employeeDescription', planned)}
            autoTranslateAll={autoTranslateAll}
          />
          <Field
            label={g('completionDescription')}
            help={helpKey('generalCompletionDescription')}
            value={actionModule.completionDescriptionText}
            onChange={v => onChanged({completionDescriptionText: v})}
            translateTitle={g('completionDescriptionTranslateTitle')}
            translations={translations.get('completionDescriptionText')}
            onTranslationsChange={v => translations.set('completionDescriptionText', v)}
            disabled={disabled}
            plannedLanguages={getPlanned('completionDescriptionText')}
            onPlannedChange={(planned) => setPlanned('completionDescriptionText', planned)}
            autoTranslateAll={autoTranslateAll}
          />
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <HelpLabel helpKey={helpKey('generalPositiveAnswerText')}>{g('positiveAnswerText')}</HelpLabel>
              <div className="flex gap-2">
                <Input
                  value={actionModule.positiveAnswerText}
                  onChange={e => onChanged({positiveAnswerText: e.target.value})}
                  disabled={disabled}
                />
                <TranslateButton
                  dialogTitle={g('positiveAnswerTextTranslateTitle')}
                  value={translations.get('positiveAnswerText')}
                  onChange={v => translations.set('positiveAnswerText', v)}
                  plannedLanguages={getPlanned('positiveAnswerText')}
                  onPlannedChange={(planned) => setPlanned('positiveAnswerText', planned)}
                  autoTranslateAll={autoTranslateAll}
                  sourceValue={actionModule.positiveAnswerText}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <HelpLabel helpKey={helpKey('generalNegativeAnswerText')}>{g('negativeAnswerText')}</HelpLabel>
              <div className="flex gap-2">
                <Input
                  value={actionModule.negativeAnswerText}
                  onChange={e => onChanged({negativeAnswerText: e.target.value})}
                  disabled={disabled}
                />
                <TranslateButton
                  dialogTitle={g('negativeAnswerTextTranslateTitle')}
                  value={translations.get('negativeAnswerText')}
                  onChange={v => translations.set('negativeAnswerText', v)}
                  plannedLanguages={getPlanned('negativeAnswerText')}
                  onPlannedChange={(planned) => setPlanned('negativeAnswerText', planned)}
                  autoTranslateAll={autoTranslateAll}
                  sourceValue={actionModule.negativeAnswerText}
                />
              </div>
            </div>
          </div>
        </section>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <IconColorPicker
              iconName={actionModule.iconName || null}
              colorHex={actionModule.colorHex || '#10b981'}
              iconUrl={actionModule.iconUrl}
              busy={iconBusy}
              onIconChange={name => onChanged({iconName: name})}
              onColorChange={hex => onChanged({colorHex: hex})}
              onUpload={onUploadIcon}
              onDelete={onDeleteIcon}
              disabled={disabled}
            />
            <HelpText helpKey={helpKey('generalIcon')}/>
          </div>

          <section className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold">{g('titleCommunication')}</h3>
            <div className="flex flex-col gap-2">
              <HelpLabel
                helpKey={helpKey('generalDetailedEmailNotification')}
                labelClassName="flex items-center gap-2 font-normal"
              >
                <Checkbox
                  checked={actionModule.detailedEmailNotification}
                  onCheckedChange={c => onChanged({detailedEmailNotification: c === true})}
                  disabled={disabled}
                />
                {g('detailedEmailNotification')}
              </HelpLabel>
              <HelpLabel
                helpKey={helpKey('generalSummaryEmailNotification')}
                labelClassName="flex items-center gap-2 font-normal"
              >
                <Checkbox
                  checked={actionModule.summaryEmailNotification}
                  onCheckedChange={c => onChanged({summaryEmailNotification: c === true})}
                  disabled={disabled}
                />
                {g('summaryEmailNotification')}
              </HelpLabel>
            </div>
            <div className="flex flex-col gap-2">
              <EmailAddressList
                addresses={actionModule.emailNotificationAddresses}
                onChanged={next => onChanged({emailNotificationAddresses: next})}
                disabled={disabled}
              />
              <HelpText helpKey={helpKey('generalEmailNotificationAddresses')}/>
            </div>
          </section>
        </div>
      </div>

      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">{g('titleExtendedSettings')}</h3>
        <HelpLabel
          helpKey={helpKey('generalCanCompleteMultipleTimes')}
          labelClassName="flex items-center gap-2 font-normal"
        >
          <Checkbox
            checked={actionModule.canCompleteMultipleTimes}
            onCheckedChange={c => onChanged({canCompleteMultipleTimes: c === true})}
            disabled={disabled}
          />
          {g('canCompleteMultipleTimes')}
        </HelpLabel>
        <HelpLabel
          helpKey={helpKey('generalAllowSaveDraft')}
          labelClassName="flex items-center gap-2 font-normal"
        >
          <Checkbox
            checked={actionModule.allowSaveDraft}
            onCheckedChange={c => onChanged({allowSaveDraft: c === true})}
            disabled={disabled}
          />
          {g('allowSaveDraft')}
        </HelpLabel>
        <HelpLabel
          helpKey={helpKey('generalShowInEmployeeDashboard')}
          labelClassName="flex items-center gap-2 font-normal"
        >
          <Checkbox
            checked={actionModule.showInEmployeeDashboard}
            onCheckedChange={c => onChanged({showInEmployeeDashboard: c === true})}
            disabled={disabled}
          />
          {g('showInEmployeeDashboard')}
        </HelpLabel>
        <div className="flex max-w-md flex-col gap-1">
          <HelpLabel helpKey={helpKey('generalMaxUserCanComplete')}>{g('maxUserCanComplete')}</HelpLabel>
          <Input
            type="number"
            value={actionModule.maxUserCanComplete ?? ''}
            onChange={e => onChanged({maxUserCanComplete: e.target.value !== '' ? Number(e.target.value) : null})}
            disabled={disabled}
          />
        </div>
      </section>
    </div>
  )
}

function EmailAddressList({
                            addresses,
                            onChanged,
                            disabled = false,
                          }: {
  addresses: string[]
  onChanged: (next: string[]) => void
  disabled: boolean
}) {
  const {t} = useTranslation('action-modules-wizard')
  const g = (key: string) => t(`tabGeneral.${key}`)

  const update = (index: number, value: string) =>
    onChanged(addresses.map((a, i) => (i === index ? value : a)))
  const remove = (index: number) => onChanged(addresses.filter((_, i) => i !== index))
  const add = () => onChanged([...addresses, ''])

  return (
    <div className="flex flex-col gap-2">
      {addresses.length === 0 ? (
        <p className="text-sm text-muted-foreground">{g('noEmailNotificationAddresses')}</p>
      ) : (
        addresses.map((address, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              type="email"
              value={address}
              placeholder={g('emailNotificationAddressPlaceholder')}
              onChange={e => update(index, e.target.value)}
              disabled={disabled}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              aria-label={g('removeEmail')}
              onClick={() => remove(index)}
              disabled={disabled}
            >
              <X className="size-4"/>
            </Button>
          </div>
        ))
      )}
      <Button type="button" variant="outline" size="sm" className="self-start" onClick={add} disabled={disabled}>
        <Plus className="size-4"/>
        {g('addEmail')}
      </Button>
    </div>
  )
}

function Field({
                 label,
                 help,
                 value,
                 onChange,
                 translateTitle,
                 translations,
                 onTranslationsChange,
                 disabled = false,
                 plannedLanguages,
                 onPlannedChange,
                 autoTranslateAll,
               }: {
  label: string
  help: string
  value: string
  onChange: (v: string) => void
  translateTitle: string
  translations: Record<string, string>
  onTranslationsChange: (v: Record<string, string>) => void
  plannedLanguages: PlannedLanguages
  onPlannedChange: (planned: PlannedLanguages) => void
  autoTranslateAll: boolean
  disabled: boolean
}) {
  return (
    <div className="flex flex-col gap-1">
      <HelpLabel helpKey={help}>{label}</HelpLabel>
      <div className="flex items-start gap-2">
        <RichTextEditor disabled={disabled} value={value} onChange={onChange} className="min-w-0 flex-1"/>
        <TranslateButton
          dialogTitle={translateTitle}
          value={translations}
          onChange={onTranslationsChange}
          plannedLanguages={plannedLanguages}
          onPlannedChange={onPlannedChange}
          autoTranslateAll={autoTranslateAll}
          sourceValue={value}
          isRichText
        />
      </div>
    </div>
  )
}
